import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import BadgePopup from "../components/Badgepopup";
import { Link } from "react-router-dom";

function Lightning92() {
  const contentRef = useRef(null);
  const titleText = "Setting up a Lightning Wallet (Hands on)";
  const questions = [
    {
      text: "1. When setting up the Phoenix wallet for the first time, what is the very first step a user must complete?",
      options: [
        {
          value: "A",
          label: "A. Generate a Lightning invoice",
        },
        {
          value: "B",
          label:
            "B. Download and install the wallet application on their device",
        },
        {
          value: "C",
          label: "C. Scan a QR code from another wallet",
        },
        {
          value: "D",
          label: "D. Import an existing Lightning channel",
        },
      ],
      correct: "B",
    },
    {
      text: "2. What happens after Phoenix generates a Lightning QR code when the user taps the “Receive” button?",
      options: [
        {
          value: "A",
          label: "A. The wallet begins opening a new channel automatically",
        },
        {
          value: "B",
          label:
            "B. The QR code must be scanned or paid to complete the incoming transaction",
        },
        {
          value: "C",
          label: "C. The wallet sends sats to another user",
        },
        {
          value: "D",
          label: "D. The user must manually create an on-chain addres",
        },
      ],
      correct: "B",
    },
    {
      text: "3. How does multi-hop routing work in the Lightning Network?",
      options: [
        {
          value: "A",
          label: "A. Payments are split into multiple cryptocurrencies",
        },
        {
          value: "B",
          label:
            "B. Payments travel across a network of connected channels using intermediaries",
        },
        {
          value: "C",
          label: "C. Payments are broadcast to all nodes on the blockchain",
        },
        {
          value: "D",
          label: "D. Payments require trust in every intermediary",
        },
      ],
      correct: "B",
    },
    {
      text: "4. . What is the role of a Hashed Timelock Contract (HTLC) in Lightning payments?",
      options: [
        {
          value: "A",
          label: "A. It creates a permanent Bitcoin address for a user",
        },
        {
          value: "B",
          label:
            "B. It locks a payment conditionally, ensuring atomic and secure transfers across multiple hops",
        },
        {
          value: "C",
          label: "C. It generates new Bitcoin blocks",
        },
        {
          value: "D",
          label: "D. It increases the block size of Bitcoin",
        },
      ],
      correct: "B",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={92}
        timerMinutes={90}
        currentLevel={7}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 72</b>! In today's chapter we take our first practical
        steps into the world of the Lightning Network by exploring what a
        Lightning wallet is, the different types of wallets you can choose, how
        to download and install a true non-custodial wallet and how to set it
        up. We then use the Phoenix Wallet as a working example: how to back up
        your recovery phrase, how the app handles payment channels, how to
        receive and send payments, and how you can begin transacting with
        Lightning in a self-custody way. By the end of this section you should
        feel confident to install a wallet, claim ownership of your keys, fund
        it, and make your first Lightning payment.
        <br />
        <br />
        <b>What is a Lightning Wallet</b>
        <br />
        <br />
        A Lightning wallet is a special kind of Bitcoin wallet designed to work
        with Lightning the layer-2 payment network built on top of the base
        Bitcoin layer. While a regular Bitcoin wallet allows you to send and
        receive on-chain transactions that are recorded directly on the
        blockchain, a Lightning wallet enables you to send and receive payments
        off‐chain, via payment channels, with extremely low fees and
        near-instant settlement. The wallet interacts with the Lightning Network
        so your funds can be routed through channels, so that even if you are
        not directly connected to someone you want to pay, the payment can
        travel via other channels in the network.
        <br />
        <br />
        <b>Types of Lightning Wallets</b>
        <br />
        <br />
        Lightning wallets therefore serve as the entry point for everyday
        payments using Bitcoin with the speed and cost structure expected of
        modern commerce. They come in different flavors: <br />
        <br />
      </p>
      <table className="trade-table ">
        <thead>
          <tr>
            <th>Type of Lightning Wallet</th>
            <th>How it Works</th>
          </tr>
          <tr>
            <td>Non custodial wallet</td>
            <td>
              User receives a seed phrase (mnemonic). All transactions and
              Lightning channels are controlled by the user’s device.
            </td>
          </tr>
          <tr>
            <td>Custodial Wallet</td>
            <td>
              The user creates an account, and the provider manages backups,
              security, and often Lightning channels.
            </td>
          </tr>
          <tr>
            <td>Hybrid Wallet</td>
            <td>
              These wallets (e.g., Phoenix, Breez) give the user full custody
              while automating complex Lightning functions like channel
              creation, inbound liquidity, and fee management.
            </td>
          </tr>
        </thead>
        <tbody></tbody>
      </table>
      <p className="content">
        The key advantage of a non-custodial Lightning wallet is that you hold
        your private keys (or seed phrase) and you are in control of your funds,
        aligning with the principle “not your keys, not your coins”.
        <br />
        When choosing a Lightning wallet, you’ll want to consider ease of use,
        control of keys, channel and liquidity management, and the support of
        routing payments in the Lightning network. Some wallets handle the
        complexity for you by automating channel opening, closing and liquidity
        balancing. Others give you manual control and require you to understand
        and manage channels yourself. For a beginner, a wallet that hides some
        of the complexity while still ensuring you hold the keys is often the
        best choice.
        <br />
        Broadly speaking, Lightning wallets can be classified into three
        categories based on custody and control. The first category is custodial
        wallets. In a custodial model, the wallet provider controls your funds
        and keys. You trust the provider to manage channels, hold liquidity,
        handle routing and settle payments. This model offers convenience and
        speed of setup but comes at the cost of giving up control. Because the
        provider holds your keys, there is counter-party risk: you must trust
        the provider not to mismanage funds, become insolvent, or be subject to
        regulatory or security issues.
        <br />
        The second category is non‐custodial wallets. Here you hold your own
        private keys (or at least the recovery phrase that gives access to
        them). The wallet handles channels and Lightning operations, but you
        retain control of the funds. You are responsible for backing up the
        seed, securing your device, and understanding how to recover funds in
        worst-case scenarios. Non-custodial wallets align strongly with the
        ethos of Bitcoin: self-custody, independence from intermediaries,
        censorship resistance and full ownership of your value.
        <br />
        The third category is hybrid or managed wallets: they may give you some
        key control but rely on a service for liquidity or channel management;
        or they may be non-custodial but still rely on centralised routing
        infrastructure. These wallets offer a middle ground between full custody
        and convenience. Some may automatically open channels for you or
        maintain a set of nodes you use rather than directing you to run your
        own. As you progress in your understanding of Lightning, you can migrate
        from a hybrid wallet to a fully self‐run solution (running your own
        node, manually managing channels) but as a beginner your first
        experience can be with a non-custodial wallet that automates much of the
        complexity.
        <br />
        It is important to know that although all categories of wallets allow
        you to send and receive Lightning payments, only non‐custodial wallets
        give you the full ownership and control of your funds. If a custodial
        provider goes offline or freezes withdrawals, your funds may be
        inaccessible. If you lose access to a non‐custodial wallet and haven’t
        backed up your recovery seed, you risk losing your funds permanently.
        Hence, while non-custodial wallets require a little extra care, they
        provide the strongest guarantee of sovereignty.
        <br /> <br />
        <b>Popular Lightning Wallets</b>
        <br />
        <br />
        There are several popular Lightning wallets available today, each
        offering different strengths and trade-offs depending on the user’s
        needs. Among the widely used options for mobile users are Phoenix
        Wallet, BlueWallet, Breez, Muun, Wallet of Satoshi, and several others.
        Some of these wallets are custodial, where a service provider controls
        the user’s keys, while others are non-custodial, giving the user full
        ownership. Some wallets offer advanced Lightning features with greater
        control, while others are designed to be simple and beginner-friendly.
        <br />
        The Phoenix Wallet is a non-custodial Lightning wallet designed with
        ease of use in mind. It gives you full control over your funds through a
        seed phrase, yet handles the technical complexities of Lightning in the
        background. It automatically manages your channels, routing, and
        liquidity balancing, giving you the experience of a seamless Lightning
        wallet while still keeping your custody intact. Phoenix runs a
        lightweight Lightning node on your phone, allowing you to maintain
        control while enjoying the convenience of automated channel operations.
        <br />
        By contrast, Wallet of Satoshi is a custodial wallet. It is extremely
        simple to use, making it attractive for beginners, but it does not give
        you access to your private keys. For people who want the easiest
        possible experience, this kind of wallet may suffice, but when you are
        ready to take full ownership of your bitcoin, a non-custodial wallet
        like Phoenix becomes the natural next step.
        <br />
        When choosing a Lightning wallet, it helps to reflect on your
        priorities. Do you want to control your own keys, or do you prefer
        absolute convenience even if it means relying on a third party? Are you
        comfortable taking on a bit more personal responsibility,such as backing
        up your seed phrase and understanding the basics of channel management
        in exchange for full ownership of your funds? Do you expect to send and
        receive many small Lightning payments, or only occasional larger ones?
        How important are speed, mobility, privacy, and security to your needs?
        These considerations will help you determine which wallet is most
        suitable for your situation.
        <br />
        For the rest of this chapter, we will use Phoenix Wallet as our example
        of how to set up a non-custodial Lightning wallet and begin making
        Lightning payments.
        <br />
        <br />
        <b>Downloading and Installing Phoenix Wallet</b>
        <br />
        <br />
        To begin using Phoenix Wallet, the first step is to install the
        application on your mobile device. The installation process is
        straightforward and does not require any prior experience with Bitcoin
        or the Lightning Network. If you are using an Android device, you can
        access the wallet through your phone’s official app store. By opening
        the store and typing “Phoenix Wallet” into the search bar, you will see
        the app appear in the list of available applications. It is important to
        select the correct app, as multiple Bitcoin-related applications may
        appear with similar names. Once you identify it, tap the install button
        and allow your device to download and install it.
      </p>
      <img src="pheonix_play_store.png"></img>
      <p className="content">
        The installation is usually quick because the application is relatively
        small in size. After installation, you will see an option to open the
        app immediately, or you can find it on your home screen like any other
        installed application. When you open Phoenix for the first time, the app
        will launch a basic onboarding sequence designed to introduce you to how
        the wallet operates. Its purpose is to d ensure that you understand the
        key elements of using a non-custodial Lightning wallet.
        <br />
        <br />
      </p>

      <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
        <img src="pheonix1.jpeg" alt="Phoenix 1" style={{ width: "250px" }} />
        <img src="pheonix2.jpeg" alt="Phoenix 2" style={{ width: "250px" }} />
        <img src="pheonix3.jpeg" alt="Phoenix 3" style={{ width: "250px" }} />
      </div>
      <p className="content">
        <br />
        <br />
        <b>Backups and Self-Custody</b>
        <br />
        <br />
        When you choose a non-custodial Lightning wallet, you take personal
        responsibility for the security of your funds. The wallet does not rely
        on any company or service provider to safeguard your bitcoin. Instead,
        the keys that control access to your funds are generated and stored
        directly on your device. This makes the backup process extremely
        important, because there is no way to recover the wallet if the keys are
        lost. <br />
        You will be asked to either create a new wallet or restore an exist
        wallet, for the purpose of learning we will assume that the user does
        not already has a wallet and intends to create one.
        <br />
        The moment you create a new wallet, you will be taken to the main screen
        of the wallet. At this point, you will typically see your balance
        displayed as 0 sats because no funds have been added yet. This is normal
        for all newly created wallets. From this screen you can explore payment
        options, settings, and the receive feature, which you will use when you
        are ready to add bitcoin to your wallet. damaged, or reset.
        <br />
      </p>
      <div style={{ display: "flex", flexDirection: "row", gap: "40px" }}>
        <img src="pheonix4.jpeg" alt="Phoenix 1" style={{ width: "250px" }} />
        <img src="pheonix5.jpeg" alt="Phoenix 2" style={{ width: "250px" }} />
      </div>

      <p className="content">
        {" "}
        On visiting the settings section you will be taken to the setting page.
        Where you can configure various features of your wallet and in this
        section you will find your 12 recovery phrase.
        <br />
        When the recovery phrase is displayed, you must carefully write it down
        on paper or another offline medium. Since this phrase grants full access
        to your funds, it must be stored in a secure location that cannot be
        accessed by unauthorized people or compromised by hackers. For long-term
        safety, some people prefer storing the phrase on a metal plate because
        it is more durable than paper and resistant to fire, water, and physical
        damage. You should avoid storing the seed phrase digitally. This
        includes cloud storage, email accounts, text files, photos, or
        screenshots, because digital devices and online services can be hacked,
        infected with malware, or breached without your knowledge. By keeping
        the backup strictly offline, you minimize these risks.
      </p>
      <div style={{ display: "flex", flexDirection: "row", gap: "40px" }}>
        <img src="pheonix6.jpeg" alt="Phoenix 1" style={{ width: "250px" }} />
        <img src="pheonix7.jpeg" alt="Phoenix 2" style={{ width: "250px" }} />
      </div>
      <p className="content">
        It is wise to store your seed phrase in more than one secure location.
        For example, you might keep one copy in a safe at home and another in a
        safety deposit box. This protects you from scenarios like fire, theft,
        or accidental loss of a single backup. Redundancy ensures that you can
        recover your wallet under different circumstances. As an additional
        precaution, once your seed phrase is backed up, you may test your
        recovery procedure if you feel comfortable doing so. This involves
        installing the wallet on another device and attempting to restore it
        using your seed phrase. Completing this test confirms that your backup
        is correct and gives you the confidence that you can recover your funds
        if something happens to your primary device.
        <br />
        Finally, it is helpful to think about how you will manage your funds
        over time. Mobile Lightning wallets are convenient for daily
        transactions and small to moderate balances, but they are not intended
        for storing large amounts of bitcoin for long periods. If your balance
        grows significantly or you begin accumulating savings, you may want to
        transfer those funds to a cold storage wallet that is better suited for
        long-term self-custody. This ensures that your day-to-day spending
        wallet remains lightweight and focused on payments, while your long-term
        savings remain protected in a more secure environment.
        <br />
        <br />
        <b>Sending and Receiving Bitcoin on the Lightning Network</b>
        <br />
        <br />
        Once your non-custodial Lightning wallet is set up and secured, the next
        step is learning how to receive and send sats using the Lightning
        Network. Since the Lightning Network is designed for fast, low-cost
        payments, it operates differently from traditional on-chain
        transactions. The process is simple once you understand the flow, and
        Phoenix makes it accessible even for beginners.
        <br />
        <br />
      </p>
      <img src="pheonix8.jpeg" width="250px"></img>
      <br />
      <br />
      <p className="content">
        To receive sats in your Lightning wallet, you will begin from the main
        home screen of the app, where your balance initially reads 0 sats.
        Assuming you already own some bitcoin in another wallet or exchange, you
        can move a portion of it into your Lightning wallet for everyday
        spending. To begin the process, tap the Receive button inside the
        Phoenix app. When you do this, the wallet will generate a QR code. This
        QR code represents a Lightning invoice. You can receive sats by letting
        the sender scan this QR code or by copying the invoice text and sharing
        it through a messaging app. Once the invoice is paid, the payment will
        settle, and your Lightning balance will update to reflect the incoming
        sats. The speed of this process showcases the core advantage of the
        Lightning Network compared to waiting for confirmations on the base
        layer.
        <br />
        Phoenix provides two types of invoices depending on your needs: BOLT 11
        and BOLT 12. A BOLT 11 invoice is a traditional Lightning invoice that
        is valid for a single payment. Once used, it cannot be reused. This is
        useful for one-time transfers where you want full control over a
        specific amount or have a single sender. BOLT 12 invoices, on the other
        hand, are reusable. This means you can generate one static QR code and
        share it repeatedly with multiple senders at different times. Although
        reusable, this format is designed in a way that does not compromise your
        privacy. Each incoming payment is routed through a process that prevents
        the sender from learning sensitive information about your wallet, while
        still giving you the flexibility of a static invoice. For merchants,
        content creators, and frequent receivers, BOLT 12 can be a significant
        convenience.
        <br />
        Once you have funds available in your Lightning wallet, you can send
        sats to others in a very similar way. To send bitcoin over Lightning,
        the recipient must first provide you with a Lightning invoice or a BOLT
        12 offer. From the home screen, tap the Send button. You will then have
        the option to scan a QR code using your phone’s camera or paste the
        invoice manually. After scanning or pasting, the wallet will read the
        invoice details, including the amount that needs to be paid. You can
        then confirm the payment. Lightning payments settle almost instantly, so
        in most cases the recipient will receive your sats within seconds.
        <br />
        If the invoice specifies the amount, the wallet will automatically fill
        in the payment details. If it is a “no-amount invoice,” you will be
        asked to enter the number of sats you want to send before confirming.
        Once confirmed, the wallet attempts to route the payment through the
        Lightning Network. If successful, your balance decreases immediately and
        a confirmation message appears. If routing cannot find a path, perhaps
        due to insufficient liquidity or temporary network issues, the payment
        may fail, in which case you can try again or ask the recipient to
        generate a new invoice.
        <br />
        Together, the Receive and Send functions form the core of Lightning
        usage. By receiving through BOLT 11 or BOLT 12 invoices and sending
        payments by scanning or pasting invoices from others, you gain access to
        a fast, global, low-fee payment system that works on top of Bitcoin.
        With just a few taps, you can transact with anyone in the world who also
        uses the Lightning Network, making Bitcoin practical for day-to-day
        payments, tipping, and micro-transactions. <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Setting up a Lightning wallet is a simple and intuitive process that
        prepares users to send and receive bitcoin instantly. After installing
        the app and completing the initial setup, users can generate a Lightning
        invoice or QR code to receive sats directly into their wallet. Phoenix
        supports both BOLT 11 one-time invoices and BOLT 12 reusable offers,
        giving users flexibility without compromising privacy. Once funds
        arrive, sending sats is equally straightforward: users scan a Lightning
        invoice or paste the payment request, confirm the details, and the
        payment settles within seconds. With these basic steps, anyone can begin
        using Bitcoin over the Lightning Network for fast and efficient
        transactions.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
      <div className="button-container">
        <Link to="/lightning-91">
          <button className="page-button">Go Back</button>
        </Link>
        <Link to="/index-lightning">
          <button className="page-button">Index</button>
        </Link>
        <Link to="/lightning-93">
          <button className="page-button">Continue</button>
        </Link>
      </div>
    </div>
  );
}

export default Lightning92;
